/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */

import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import type { Metric } from "@unbox/api";
import { Calendar } from "lucide-react";
import type React from "react";
import { useEffect, useMemo } from "react";
import { useDateLimits } from "@/api/date-limits";
import { DateRangePicker as DateRangeSelector } from "@/components/application/date-picker/date-range-picker";
import { Button } from "@/components/base/buttons/button";
import { DateGranularitySelector } from "@/components/DateGranularitySelector";
import { MetricsTableView } from "@/components/MetricsTableView";
import { CURRENCY_METRICS, METRIC_COLORS, METRICS_MAP, PERCENT_METRICS } from "@/constants/metrics";
import { useReportFormatters } from "@/hooks/use-report-formatters";
import { useProcessedMetricsData } from "../hooks/use-processed-metrics-data";
import { useReportDateFilter } from "../hooks/use-report-date-filter";

export const getMetricDisplayInfo = (metric: Metric) => {
  // Use shared metrics config with local overrides
  const localOverrides: Partial<Record<Metric, { label: string; description: string }>> = {
    unitSales: { label: "Units Sold", description: "Units sold" },
  };

  return localOverrides[metric] || METRICS_MAP[metric];
};

// CSV export helper function
export const exportToCSV = (data: any[], metrics: Metric[], reportName: string) => {
  if (data.length === 0) return;

  // Format values for CSV (remove formatting for raw data)
  const formatValueForCSV = (value: any) => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return "";
    }
    return Number(value);
  };

  // Create CSV content
  const headers = ["Metric", ...data.map((d) => d.dateLabel)];
  const csvRows = [headers.join(",")];

  // Add each metric as a row
  metrics.forEach((metric) => {
    const metricInfo = getMetricDisplayInfo(metric);
    const row = [
      `"${metricInfo.label}"`,
      ...data.map((dataPoint) => formatValueForCSV(dataPoint[metric as keyof typeof dataPoint])),
    ];
    csvRows.push(row.join(","));
  });

  const csvContent = csvRows.join("\n");

  // Create and download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${reportName.replace(/[^a-z0-9]/gi, "_")}_report.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const TableReport: React.FC<{
  metrics: Metric[];
  createdAt: string;
  updatedAt: string;
  reportId: string;
  reportName: string;
  onExport?: (data: any[], metrics: Metric[], reportName: string) => void;
  fixedStartDate?: string;
  fixedEndDate?: string;
}> = ({ metrics, createdAt, updatedAt, reportId, reportName, onExport, fixedStartDate, fixedEndDate }) => {
  const { dateRangeMode, setDateRangeMode, dateRange, setDateRange, timePeriod, hasFixedDates } = useReportDateFilter(
    reportId,
    "table",
    fixedStartDate,
    fixedEndDate,
  );

  // Date limits for restricting selectable dates
  const { data: dateLimits } = useDateLimits();
  const limits = useMemo(() => {
    if (!dateLimits) return undefined;
    return {
      minDate: parseDate(dateLimits.minDate),
      maxDate: parseDate(dateLimits.maxDate),
    };
  }, [dateLimits]);

  // Use common data processing hook (with gap filling for fixed date reports)
  const { filteredData: chartData } = useProcessedMetricsData(metrics, timePeriod, dateRange, hasFixedDates);

  // Build table config
  const { gmv: gmvFormatter, video: videoFormatter, percent: percentFormatter } = useReportFormatters();

  const tableConfig = useMemo(() => {
    return {
      metrics: metrics,
      formatters: Object.fromEntries(
        metrics.map((m) => [
          m,
          CURRENCY_METRICS.includes(m)
            ? (v: number) => gmvFormatter.format(v)
            : PERCENT_METRICS.includes(m)
              ? (v: number) => percentFormatter.format(v)
              : (v: number) => videoFormatter.format(v),
        ]),
      ),
      colors: Object.fromEntries(metrics.map((m) => [m, METRIC_COLORS[m] || "#6b7280"])),
      labels: Object.fromEntries(metrics.map((m) => [m, METRICS_MAP[m]?.label || m])),
    };
  }, [metrics, gmvFormatter, videoFormatter, percentFormatter]);

  // Update parent with current data whenever it changes
  useEffect(() => {
    if (onExport && chartData.length > 0) {
      onExport(chartData, metrics, reportName);
    }
  }, [onExport, chartData, metrics, reportName]);

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Table Section with Border */}
      <div className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6 gap-4">
        {/* Controls Section */}
        <div className="flex items-center justify-end flex-shrink-0" data-no-export>
          <div className="flex items-center gap-3">
            {hasFixedDates && dateRange ? (
              /* Fixed date range display (read-only) */
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                <span>
                  {dateRange.start.toDate(getLocalTimeZone()).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}{" "}
                  -{" "}
                  {dateRange.end.toDate(getLocalTimeZone()).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
            ) : (
              <>
                {/* Date Range Selector */}
                <DateRangeSelector
                  value={dateRange}
                  onChange={setDateRange}
                  selected={dateRange ? { start: dateRange.start, end: dateRange.end } : null}
                  isActive={!!dateRange}
                  textClassName="text-sm font-semibold"
                  buttonClassName="h-10"
                  aria-label="Date range picker"
                  shouldCloseOnSelect={false}
                  onApply={() => {
                    // Date range is already set via onChange
                  }}
                  onReset={() => {
                    // Default to last 31 days
                    const now = today(getLocalTimeZone());
                    const thirtyOneDaysAgo = now.subtract({ days: 31 });
                    setDateRange({ start: thirtyOneDaysAgo, end: now });
                  }}
                  showPresets={true}
                  resetText="Last 31 days"
                  limits={limits}
                />
              </>
            )}

            {/* Time Step Pills */}
            <DateGranularitySelector value={dateRangeMode} onChange={setDateRangeMode} />
          </div>
        </div>

        {/* Table Content */}
        <MetricsTableView data={chartData as any} config={tableConfig as any} />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-500 flex-shrink-0" data-no-export>
        <div>
          Last edited:{" "}
          {new Date(updatedAt).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
          }) +
            " @" +
            new Date(updatedAt).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })}
        </div>
        <div>
          Created:{" "}
          {new Date(createdAt).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
          })}
        </div>
      </div>
    </div>
  );
};
